import { FC, Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon, PlayIcon } from "@heroicons/react/24/outline";
import ReactPlayer from 'react-player'

// Components
import { ListingGalleryImage } from "components/ListingImageGallery/utils/types";
import { SectionSocials } from "components/SectionSubscribe2/SectionSocials";
import { FinanceSlider } from "containers/CarProfilePage/FinanceSlider";
import { EditableNumber } from "components/FilterForm/EditableNumber";
import EnquirePopUp from "containers/CarProfilePage/EnquirePopup";
import ProfileMetaTags from "containers/CarProfilePage/ProfileMetaTags";

// Custom handlers
import { generateDescription, getFinance, getStockbyId } from "api/stock";
import { Cars, FinanceRequest, technicalDetailLabels, vechicleDetailsLabels } from "data/types";
import { convertToNumber, getCarIdFromUrl } from "utils/utils";
import convertNumbThousand from "utils/convertNumbThousand";
import { isNumber } from "lodash";

import Loading from "components/Loading/Loading";
import GallerySlider from "components/GallerySlider/GallerySlider";

const SaleOffBadge = lazy(() => import("components/SaleOffBadge/SaleOffBadge"));
const CTAButton = lazy(() => import("containers/CarProfilePage/CTAButton"));
const SectionSubscribe2 = lazy(() => import("components/SectionSubscribe2/SectionSubscribe2"));
const SocialsList = lazy(() => import("shared/SocialsList/SocialsList"));
const ListingImageGallery = lazy(() => import("components/ListingImageGallery/ListingImageGallery"));
const ButtonPrimary = lazy(() => import("shared/Button/ButtonPrimary"));
const Accordion = lazy(() => import("containers/CarProfilePage/FeaturesAccordion"));

const CarProfile: FC<{}> = () => {
    // URL params
    const thisPathname = useLocation().pathname;
    const [activeTab, setActiveTab] = useState<"details" | "finance" | "insurance" | "warranty">("details");

    const router = useNavigate();
    const [searchParams] = useSearchParams();
    const modal = searchParams?.get("modal");
    const { carId } = useParams();

    // Finance options refs
    const [financeRequest, setFinanceRequest] = useState<FinanceRequest>({
        AnnualDistance: 9000,
        Term: 48,
        IdentityType: "VRM",
    });
    const [financeData, setFinanceData] = useState<any>();

    // Get car info
    const { isLoading, error, data, isFetching } = useQuery({
        queryKey: ["stock", getCarIdFromUrl(carId!)],
        queryFn: () => getStockbyId(getCarIdFromUrl(carId!)!),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    const [features, setFeatures] = useState<any>([false, false]);
    const [carInfo, setCarInfo] = useState<Cars>({} as Cars);
    const [isParsing, setIsParsing] = useState<boolean>(true);
    const [activeFinance, setActiveFinance] = useState<"hp" | "pcp">("pcp");

    // Car photos
    const [PHOTOS, setPHOTOS] = useState<string[]>([]);
    const [video, setVideo] = useState<string>();
    const [isWatchingVideo, setIsWatchingVideo] = useState<boolean>(false);

    const [genDesc, setGenDesc] = useState<{ triggered: boolean, text: string }>({
        triggered: false,
        text: ""
    });

    useEffect(() => {
        setIsParsing(true);
        if (data) setCarInfo(data[0].results[0]);
    }, [data])

    // Parse car info
    useEffect(() => {
        if (carInfo && carInfo.media && carInfo.media.images) {
            setPHOTOS(carInfo.media.images.map((item: any) => {
                return item.href;
            }))
        }

        if (carInfo && carInfo.media && carInfo.media.video) {
            setVideo(carInfo.media.video.href);
        }

        if (carInfo) {
            setFinanceRequest({
                ...financeRequest,
                CashDeposit: convertToNumber(carInfo.priceGBP) * 0.1,
                CashPrice: convertToNumber(carInfo.priceGBP),
                CurrentOdometerReading: parseInt(carInfo.mileage!) ? parseInt(carInfo.mileage!) : 0,
                Identity: carInfo.registration,
                RegistrationDate: carInfo.firstRegistrationDate ? carInfo.firstRegistrationDate : new Date().toISOString(),
                Condition: carInfo.ownershipCondition ? carInfo.ownershipCondition : "Used",
            })

            // setFinanceData(carInfo.monthlyPayment)
        }

        if (carInfo.features) {
            parseFeatures(data[1].features);
        }
    }, [carInfo])

    useEffect(() => {
        if (financeRequest && financeRequest.CashPrice) handleFinanceOptions()
    }, [financeRequest])

    useEffect(() => {
        if (isFetching || isLoading || Object.values(carInfo).length === 0) return;

        if (carInfo && genDesc.triggered === false && !isParsing) {
            setGenDesc({
                triggered: true,
                text: ""
            })

            // generate description
            generateDescription(carInfo, features[1]).then((res: any) => {
                setGenDesc({
                    triggered: true,
                    text: res
                })
            })
        }
    }, [carInfo, isLoading, isFetching, features, isParsing])

    // Helper functions
    const handleFinanceOptions = async () => {
        const quote = await getFinanceMemoized();
        setFinanceData(quote);
    }

    const parseFeatures = (features: any) => {
        const standardParseFeatures: any = {};
        const optionalParseFeatures: any = [];

        if (!features) {
            setFeatures([{}, false]);
            setIsParsing(false);
            return;
        }

        features.forEach((item: any) => {
            const featureExists = carInfo.features && carInfo.features.some((feat: any) => feat.name === item.name)

            if (item.type === 'Standard') {
                if (!standardParseFeatures[`${item.category}`]) standardParseFeatures[`${item.category}`] = [];

                if (featureExists) {
                    standardParseFeatures[`${item.category}`].push(item);
                }
            } else {
                if (featureExists && item.basicPrice) {
                    optionalParseFeatures.push(item);
                }
            }

        })

        setFeatures([standardParseFeatures, optionalParseFeatures]);
        setIsParsing(false);
    }

    const handleOpenModalImageGallery = (photoId?: number) => {
        if (isNumber(photoId)) router(`${thisPathname}?modal=PHOTO_TOUR_SCROLLABLE&photoId=${photoId}`);
        else router(`${thisPathname}?modal=PHOTO_TOUR_SCROLLABLE`);
    };

    const handleCloseModalImageGallery = () => {
        let params = new URLSearchParams(document.location.search);
        params.delete("modal");
        window.history.pushState({}, '', '/cars');
        router(`${thisPathname}?${params.toString()}`);
    };

    const getFinanceMemoized = useMemo(() => {
        const getFinanceData = async () => {
            return await getFinance(financeRequest);
        };

        return getFinanceData;
    }, [financeRequest]);
    // End helper functions

    const renderVehicleInfo = () => {
        return (
            <div className="w-full flex flex-col lg:flex-row gap-10 justify-between">
                <div className={`flex flex-col gap-4 ${(features[1] && features[1].length > 0) ? "" : "w-full"}`}>
                    <h2 className="text-lg font-semibold text-red-600 uppercase">
                        specifications
                    </h2>

                    <div className="flex flex-col justify-between gap-y-5 gap-x-10 text-sm text-neutral-700 dark:text-neutral-300">
                        {carInfo.yearOfManufacture ? (
                            <div className="flex items-center text-left space-x-3">
                                <i className="fal fa-calendar text-xl"></i>
                                <span className="flex flex-row gap-4 justify-between w-full">
                                    <span className={`font-normal ${(features[1] && features[1].length > 0) ? "min-w-[150px]" : "w-full"}`}>Registration:</span>
                                    <strong className="font-extrabold text-white text-left w-full">{carInfo.yearOfManufacture} {`${carInfo.plate ? `(${carInfo.plate})` : ''}`}</strong>
                                </span>
                            </div>
                        ) : null}

                        {carInfo && Object.keys(carInfo).map((key: string, index: number) => {
                            if (!vechicleDetailsLabels[key]) return null

                            if (!Object.values(carInfo)[index]) return null

                            return (
                                <div className="flex items-center text-left space-x-3" key={index}>
                                    <i className={`${vechicleDetailsLabels[key]?.icon} text-lg`}></i>
                                    <span className="flex flex-row gap-4 justify-between w-full font-extralight">
                                        <span className={`font-normal ${(features[1] && features[1].length > 0) ? "min-w-[150px]" : "w-full"}`}>{vechicleDetailsLabels[key].label}:</span>
                                        <strong className="font-extrabold text-white text-left w-full">
                                            {Object.values(carInfo)[index] ? `${vechicleDetailsLabels[key].isNumber ? convertNumbThousand(Object.values(carInfo)[index]) : Object.values(carInfo)[index]} ${vechicleDetailsLabels[key].suffix}` : "N\\A"}
                                        </strong>
                                    </span>
                                </div>
                            )
                        })}

                        {carInfo.technicalDetails && Object.keys(carInfo.technicalDetails).map((key: string, index: number) => {
                            if (!technicalDetailLabels[key]) return null

                            if (!Object.values(carInfo.technicalDetails)[index]) return null

                            return <div className="flex items-center text-left space-x-3" key={index}>
                                <i className={`${technicalDetailLabels[key].icon} text-lg`}></i>
                                <span className="flex flex-row gap-4 justify-between w-full font-extralight">
                                    <span className={`font-normal ${(features[1] && features[1].length > 0) ? "min-w-[150px]" : "w-full"}`}>{technicalDetailLabels[key].label}:</span>
                                    <strong className="font-extrabold text-white text-left w-full">
                                        {Object.values(carInfo.technicalDetails)[index] ? `${Object.values(carInfo.technicalDetails)[index]} ${technicalDetailLabels[key].suffix}` : "N\\A"}
                                    </strong>
                                </span>
                            </div>
                        })}
                    </div>
                </div>

                {(features[1] && features[1].length > 0) && (
                    <div className="flex flex-col gap-4">
                        <h2 className="text-lg font-semibold text-red-600 uppercase">
                            Optional Extras
                        </h2>
                        <div className="grid grid-cols-1 gap-y-4 gap-x-10 text-sm text-neutral-700 dark:text-neutral-300">
                            {features[1].map((item: any, idx: number) => {
                                return <div className="flex items-center text-left space-x-3" key={idx}>
                                    <div key={item.name} className="flex space-x-2 items-start">
                                        <span className="font-bold font-display text-md text-white">{idx + 1 > 9 ? idx + 1 : `0${idx + 1}`}</span>
                                        <p className='item w-full h-auto text-sm'>
                                            {item.name}
                                            <small className="block text-neutral-500 dark:text-neutral-400">{item.category}</small>
                                        </p>
                                    </div>
                                </div>
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderDescription = (mobile: boolean = false) => {
        // return;
        return (
            <div className={`max-w-[1200px] mx-auto w-full mt-0 px-0 xl:px-0 text-center ${mobile ? "block lg:hidden text-justify text-xl" : "hidden lg:block text-2xl"}`}>
                {(genDesc.text === "") && (
                    <>
                        <div className="text-neutral-700 dark:text-white text-sm bg-neutral-100 dark:bg-zinc-800 animate-pulse h-3 mb-3 rounded-md"></div>
                        <div className="text-neutral-700 dark:text-white text-sm bg-neutral-100 dark:bg-zinc-800 animate-pulse h-3 mb-3 rounded-md"></div>
                        <div className="text-neutral-700 dark:text-white text-sm bg-neutral-100 dark:bg-zinc-800 animate-pulse h-3 mb-3 rounded-md"></div>
                        <div className="text-neutral-700 dark:text-white text-sm bg-neutral-100 dark:bg-zinc-800 animate-pulse h-3 mb-3 rounded-md"></div>
                        <div className="text-neutral-700 dark:text-white text-sm bg-neutral-100 dark:bg-zinc-800 animate-pulse h-3 mb-3 rounded-md"></div>
                        <div className="text-neutral-700 dark:text-white text-sm bg-neutral-100 dark:bg-zinc-800 animate-pulse h-3 mb-3 rounded-md"></div>
                    </>
                )}

                {genDesc.text && (<p className="text-neutral-6000 dark:text-white">
                    {genDesc.text}
                </p>)}
            </div>
        );
    };

    const renderFinanceResults = () => {
        const noFinance = <>
            <div className="py-5 mt-8 rounded-2xl flex flex-col items-center font-semibold space-y-3 dark:bg-custom-200/50 border-none">
                <h2 className="text-xl dark:text-white">Get in touch</h2>
                <span
                    className="px-3 text-neutral-700 dark:text-zinc-100 text-sm font-medium text-center"
                >
                    Contact a member of our team <br />for our bespoke finance deals.
                </span>
            </div>
            <ButtonPrimary className="w-[90%] mx-auto mt-4 Call" onClick={
                () => {
                    window.open(`tel:+44 (0)1423 798781`)
                }
            }>
                Call
            </ButtonPrimary>
        </>

        if (!financeData || (financeData.hp.length === 0 && financeData.pcp.length === 0)) return noFinance

        let data;
        switch (activeFinance) {
            case "hp":
                if (financeData.hp.length === 0) return noFinance
                data = financeData.hp[0]
                break
            case "pcp":
                if (financeData.pcp.length === 0) return noFinance
                data = financeData.pcp[0]
                break
            default:
                return null
        }

        return (
            <div className="px-0 py-5 mt-8 rounded-2xl flex flex-col items-center font-semibold space-y-1 dark:bg-custom-200/50 border-none">
                <h2 className="text-base text-center md:text-lg lg:text-lg text-red-600 uppercase font-extrabold">{activeFinance} Finance Quote</h2>
                <div className="relative w-full">
                    <span className="w-full px-3 text-neutral-700 dark:text-zinc-100 text-3xl font-normal flex flex-col items-center">
                        <div className="flex flex-col w-full my-6 px-5 items-center gap-y-3 font-body">
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">First payment of</span> £{convertNumbThousand(data.firstPayment)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">{data.numberOfRegularPayments} monthly payments of</span> £{convertNumbThousand(data.regularPayment)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Final payments of</span> £{convertNumbThousand(data.finalPayment)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Term (Months)</span> {data.term}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Cash price</span> £{convertNumbThousand(data.totalCashPrice)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Total deposit</span> £{convertNumbThousand(data.totalDeposit)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Total Amount credit</span> £{convertNumbThousand(data.advance)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Interest charges</span> £{convertNumbThousand(data.intertestCharge)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Total amount payable</span> £{convertNumbThousand(data.totalPayable)}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Fixed rate of interest
                                    <span className="ml-1 text-xs">(per annum)</span>
                                </span> {data.interestRate}%
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">APR</span> {data.apr}%
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Administration fee</span>
                                £0
                                {/* £{convertNumbThousand(data.optionToPurchaseFee)} */}
                            </div>
                            <div className="text-base lg:text-sm w-full text-neutral-500 dark:text-white font-medium flex justify-between mx-auto items-center">
                                <span className="">Option to purchase fee</span> £{convertNumbThousand(data.optionToPurchaseFee)}
                            </div>
                        </div>
                    </span>
                </div>
                {renderCallToAction()}
            </div>
        )
    }

    const renderCallToAction = () => {
        if (!financeData || (financeData.hp.length === 0 && financeData.pcp.length === 0)) return null
        if (!data || !carInfo) return null

        if (activeFinance === "hp" && financeData.hp.length === 0) {
            // if (financeData.pcp.length > 0) setActiveFinance("pcp")
            return null
        }
        if (activeFinance === "pcp" && financeData.pcp.length === 0) {
            // if (financeData.hp.length > 0) setActiveFinance("hp")
            return null
        }

        return <CTAButton
            quoteID={financeData && activeFinance === "hp" ? financeData.hp[0].quoteUID : financeData.pcp[0].quoteUID}
            returnURL={window.location.href}
            imageURL={carInfo.media.images[0].href}
        />
    }

    const renderSidebarPrice = () => {
        let depositAmounts = [
            0, 5000, 10000, 20000, 30000, 40000, 50000
        ]

        // calculate the deposit amounts based on the price of the car
        if (carInfo.priceGBP) {
            const price = convertToNumber(carInfo.priceGBP);
            let increments = 10000;

            if (price > 100000) increments = 15000;
            else if (price < 50000) increments = 5000;
            else if (price < 20000) increments = 1000;

            const maxDeposit = price * 0.4; // Maximum deposit allowed (40% of car price)
            depositAmounts = [0];

            for (let i = increments; i <= maxDeposit; i += increments) {
                if (depositAmounts.length > 5 || i >= price) break;
                depositAmounts.push(i);
            }
        }

        return (
            <div className="shadow-xl px-2 lg:px-10 flex flex-col">
                <h2 className="text-lg font-semibold text-red-600 uppercase block lg:hidden md:hidden mt-10">Finance</h2>

                {carInfo.monthlyPayment && financeData ? (
                    <>
                        <div className="flex flex-row items-center justify-center gap-2 my-4 w-full py-1 px-1 text-white border border-custom-900 dark:border-white rounded-full">
                            <button
                                className={`text-sm font-medium h-10 px-6 w-full flex items-center justify-center rounded-full cursor-pointer
                                ${activeFinance === "pcp" ? "bg-red-600" : "bg-zinc-700"}`}
                                onClick={() => {
                                    setActiveFinance("pcp")
                                }}
                            >
                                <i className="fal fa-shield-check mr-3"></i>
                                PCP
                                {` `}
                                {/* {financeData.pcp.length === 0 && <span className="text-xs dark:text-gray-300 ml-3">(unavailable)</span>} */}
                            </button>
                            <button
                                className={`text-sm font-medium h-10 px-6 w-full  flex items-center justify-center rounded-full
                    ${activeFinance === "hp" ? "bg-red-600" : "bg-zinc-700"}`}
                                onClick={() => setActiveFinance("hp")}
                            >
                                <i className="fal fa-shield-check mr-3"></i>
                                HP
                            </button>
                        </div>
                        <div className="flex flex-col mt-3">
                            <div className="flex flex-col font-semibold dark:text-white mb-2">
                                <div className="relative">
                                    <div className="flex items-center gap-2 justify-between">
                                        <span className="">Duration:</span>
                                        <div className="my-2">
                                            <EditableNumber
                                                name='Term'
                                                value={Number(financeRequest.Term)}
                                                suffix='Months'
                                                inputClass="!text-lg pl-9"
                                                min={24}
                                                max={60}
                                                onBlur={(e) => {
                                                    setFinanceRequest({ ...financeRequest, Term: parseInt(e.target.value) })
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <FinanceSlider
                                        range={[
                                            24, 36, 48, 60
                                        ]}
                                        defaultValue={financeRequest.Term}
                                        dragStrictly={true}
                                        onChange={(value: number) => setFinanceRequest({ ...financeRequest, Term: value })}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col font-semibold dark:text-white mb-2">
                                <div className="relative">
                                    <div className="flex items-center gap-2 justify-between">
                                        <span className="">Deposit:</span>
                                        <div className="">
                                            <EditableNumber
                                                name='CashDeposit'
                                                value={Number(financeRequest.CashDeposit)}
                                                prefix='£'
                                                inputClass="!text-lg pl-9"
                                                onBlur={(e) => setFinanceRequest({ ...financeRequest, CashDeposit: parseInt(e.target.value) })}
                                                max={activeFinance === "pcp" ? convertToNumber(carInfo.priceGBP) * 0.4 : convertToNumber(carInfo.priceGBP)}
                                            />
                                        </div>
                                    </div>
                                    <FinanceSlider
                                        range={depositAmounts}
                                        defaultValue={financeRequest.CashDeposit}
                                        onChange={(value: number) => setFinanceRequest({ ...financeRequest, CashDeposit: value })}
                                    />
                                </div>
                            </div>

                            {activeFinance === "pcp" && (
                                <div className="flex flex-col font-semibold dark:text-white">
                                    <div className="relative">
                                        <div className="flex items-center gap-2 justify-between">
                                            <span className="">Annual Mileage:</span>

                                            <div className="my-4">
                                                <EditableNumber
                                                    name='annualDistance'
                                                    value={Number(financeRequest.AnnualDistance)}
                                                    suffix='Miles'
                                                    inputClass="!text-lg pl-9"
                                                    onBlur={(e) => setFinanceRequest({ ...financeRequest, AnnualDistance: parseInt(e.target.value) })}
                                                />
                                            </div>
                                        </div>

                                        <FinanceSlider
                                            range={[
                                                6000, 9000, 12000, 15000, 18000, 21000, 24000, 27000, 30000
                                            ]}
                                            defaultValue={financeRequest.AnnualDistance}
                                            dragStrictly={true}
                                            onChange={(value: number) => setFinanceRequest({ ...financeRequest, AnnualDistance: value })}
                                        />
                                    </div>
                                </div>
                            )}

                            {renderFinanceResults()}
                        </div>

                    </>
                ) : (
                    <>
                        <div className="p-4 mt-4 flex flex-col items-center font-semibold space-y-3 dark:bg-custom-200/50 border-none rounded-lg">
                            <h2 className="text-xl dark:text-white">Get in touch</h2>
                            <span
                                className="block px-3 text-neutral-700 dark:text-zinc-100 text-sm font-medium text-center"
                            >
                                Contact a member of our team <br />for our bespoke finance deals.
                            </span>
                        </div>
                        <ButtonPrimary className="w-[80%] mx-auto mt-4" onClick={
                            () => {
                                window.open(`tel:+44 (0)1423 798781`)
                            }
                        }>
                            Call
                        </ButtonPrimary>
                    </>
                )
                }
            </div >
        );
    };

    const renderVehicleDetails = () => {
        if (!carInfo) return null
        return (
            <div className="relative text-neutral-900 dark:text-white flex flex-col items-start justify-center lg:h-full  w-full mt-5 lg:mt-0">
                <div className="relative z-10 flex flex-col">
                    {carInfo.meta && carInfo.meta?.advertiserId === "10035437" && (
                        <div className="w-fit my-4">
                            <SaleOffBadge desc="Cotswolds" className="bg-white dark:text-black font-bold !text-base" />
                        </div>
                    )}
                    <h2 className="text-2xl font-semibold text-left max-w-md">
                        {carInfo.make} {carInfo.model}
                    </h2>
                    <span className="text-xs mt-2 text-left text-gray-500 font-medium">
                        {carInfo.derivative}
                    </span>
                    <span className="text-xs mt-2 text-left text-gray-500 font-medium">
                        {carInfo.yearOfManufacture} {carInfo.plate ? `(${carInfo.plate})` : ""}
                    </span>
                </div>

                <div className="mt-4 flex justify-between">
                    <span className="text-4xl font-semibold">
                        £{carInfo.priceGBP}
                        {carInfo.monthlyPayment && <span className="block text-sm text-neutral-200 font-normal"> or from <span className="font-bold">{`£${carInfo.monthlyPayment}`}</span> per month</span>}
                    </span>
                </div>


                {/* features */}
                <div className="mt-4 grid grid-cols-2 xl:grid-cols-3 gap-5 text-xs text-neutral-700 dark:text-white">
                    {carInfo.yearOfManufacture && (
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center space-x-2">
                            <i className="fal fa-calendar text-xl"></i>
                            <span>{carInfo.yearOfManufacture}</span>
                        </div>
                    )}
                    {carInfo.mileage && (
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center space-x-2">
                            <i className="fa-sharp fa-light fa-tachometer-alt-fastest text-xl"></i>
                            <span>{`${convertNumbThousand(Number(carInfo.mileage))} Miles`}</span>
                        </div>
                    )}
                    {carInfo.fuelType && (
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center space-x-2">
                            <i className="fat fa-gas-pump text-xl"></i>
                            <span>{carInfo.fuelType}</span>
                        </div>
                    )}
                    {carInfo.transmissionType && (
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center space-x-2">
                            <i className="fal fa-cog text-xl"></i>
                            <span>{carInfo.transmissionType}</span>
                        </div>
                    )}
                    {carInfo.ownershipCondition && (
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center space-x-2">
                            <i className="fal fa-car text-xl"></i>
                            <span>{carInfo.ownershipCondition}</span>
                        </div>
                    )}
                    {carInfo.color && (
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center space-x-2">
                            <i className="fa-light fa-spray-can text-xl"></i>
                            <span>{carInfo.color}</span>
                        </div>
                    )}
                </div>


                <div className="flex flex-wrap gap-4 mt-6 w-full">
                    <Link to={"tel:+44 (0)1423 798781"}
                        className={"bg-transparent border Call hover:bg-custom-200 border-black dark:border-white text-black dark:text-white p-2 px-5 rounded-3xl font-medium w-full max-w-[143px] text-center text-[.6rem] font-display tracking-wider uppercase"}>
                        <i className="fal fa-phone-alt mr-2"></i>
                        Call
                    </Link>
                    <Link
                        to={"#enquire"}
                        onClick={() => {
                            //goto #enquire div
                            const el = document.getElementById("enquire");
                            if (el) {
                                el.scrollIntoView({ behavior: "smooth" });
                            }
                        }}
                        className={"bg-transparent border Vehicle_enquiry hover:bg-custom-200 border-black dark:border-white text-black dark:text-white p-2 px-5 rounded-3xl font-medium w-full max-w-[143px] text-center text-[.6rem] font-display tracking-wider uppercase"}>
                        <i className="fal fa-envelope mr-2"></i>
                        Enquire
                    </Link>
                </div>
            </div>
        )
    }

    const renderFeatures = () => {
        if (isParsing) return <div className="text-neutral-700 dark:text-white text-sm bg-neutral-100 dark:bg-zinc-800 animate-pulse h-20 rounded-md"></div>

        let newFeatures = Object.keys(features[0]).map((key: string, idx: number) => {
            return {
                title: key,
                items: features[0][key]
            }
        })

        if (newFeatures.length === 0) return null

        return (
            <div className="!space-y-6 w-full">
                <h2 className="text-xl font-semibold text-red-600 uppercase block lg:hidden md:hidden mt-10">
                    Details
                </h2>
                <Accordion content={newFeatures} />
            </div>
        )
    }

    const renderTabs = () => {
        return (
            <div className="w-full mx-auto overflow-x-hidden flex flex-col md:flex-row lg:flex-row items-center justify-center gap-2 mt-16 mb-5 py-1 px-1 text-white border border-custom-900 dark:border-white rounded-2xl md:rounded-full lg:rounded-full">
                <button
                    className={`text-xs font-display h-10 px-6 w-full uppercase flex items-center justify-center rounded-full
${activeTab === "details" ? "bg-red-500" : "bg-custom-900"}`}
                    onClick={() => setActiveTab("details")}
                >
                    <i className="far fa-info-circle mr-3"></i>
                    Details
                </button>
                <button
                    className={`text-xs font-display h-10 px-6 w-full uppercase flex items-center justify-center rounded-full
${activeTab === "finance" ? "bg-red-500" : "bg-custom-900"}`}
                    onClick={() => setActiveTab("finance")}
                >
                    <i className="far fa-money-bill-wave mr-3"></i>
                    Finance
                </button>
                <button
                    className={`text-sm font-medium h-10 px-6 w-full uppercase hidden items-center justify-center rounded-full
${activeTab === "insurance" ? "bg-red-500" : "bg-custom-900"}`}
                    onClick={() => setActiveTab("insurance")}
                >
                    <i className="fas fa-file-lines mr-3"></i>
                    Insurance
                </button>
                <button
                    className={`text-sm font-medium h-10 px-6 w-full uppercase hidden items-center justify-center rounded-full
${activeTab === "warranty" ? "bg-red-500" : "bg-custom-900"}`}
                    onClick={() => setActiveTab("warranty")}
                >
                    <i className="fas fa-wrench mr-3"></i>
                    Warranty
                </button>
            </div>
        )
    }

    const renderActiveTab = () => {
        switch (activeTab) {
            case "details":
                return renderFeatures();
            case "finance":
                return renderSidebarPrice();
            case "insurance":
                return <></>
            // return <InsuranceForm carInfo={carInfo} />;
            case "warranty":
                return <></>
            // return <WarrantyForm carInfo={carInfo} />;
            default:
                return renderVehicleInfo();
        }
    }

    if (isLoading || isFetching) return <Loading />
    if (error instanceof Error) return <h1>{error.message}</h1>

    return (
        <Suspense fallback={<Loading />}>
            <div className="dark:bg-custom-900 pb-16">
                {carInfo && data && (
                    <div className={`nc-ListingCarDetailPage relative overflow-hidden `}>
                        <ProfileMetaTags carInfo={carInfo} />
                        <div className="absolute top-12 left-0 z-10 mx-6 lg:mx-14 mt-3">
                            <button
                                onClick={() => {
                                    window.history.back();
                                }}
                                className="flex items-center justify-center w-10 h-10 focus:outline-none"
                            >
                                <span className="flex gap-2 text-sm">
                                    <ArrowLeftIcon
                                        className="h-5 w-5"
                                        title="Go back to the previous page"
                                    />
                                    Back
                                </span>
                            </button>
                        </div>

                        <ListingImageGallery
                            isShowModal={modal === "PHOTO_TOUR_SCROLLABLE"}
                            onClose={handleCloseModalImageGallery}
                            images={[...PHOTOS].map(
                                (item, index): ListingGalleryImage => {
                                    return {
                                        id: index,
                                        url: item,
                                    };
                                }
                            )}
                        />

                        <div className="bg-white dark:bg-custom-900 flex space-y-5 lg:space-y-0 flex-col lg:h-full pt-16">
                            <div className="relative flex flex-col lg:flex-row md:flex-col min-h-[260px] lg:min-h-[450px] w-full h-full gap-7 items-center justify-center">
                                <div className="relative w-screen cursor-pointer fade bg-gradient-to-r from-transparent to-custom-100 dark:to-custom-900 lg:max-w-[500px] xl:max-w-[750px]">
                                    {video && !isWatchingVideo && (
                                        <div className="absolute top-0 right-0 z-10 w-fit">
                                            <button
                                                onClick={() => setIsWatchingVideo(true)}
                                                className="flex items-center justify-center focus:outline-none"
                                            >
                                                <SaleOffBadge descElement={
                                                    <span className="flex gap-2 text-xs p-0.5 text-white">
                                                        <PlayIcon
                                                            className="h-4 w-4"
                                                        />
                                                        Watch Video
                                                    </span>
                                                } className="!bg-custom-200 dark:text-black font-semibold" />

                                            </button>
                                        </div>
                                    )}

                                    {isWatchingVideo && (
                                        <div className="absolute top-0 right-0 z-10 w-fit">
                                            <button
                                                onClick={() => setIsWatchingVideo(false)}
                                                className="flex items-center justify-center focus:outline-none"
                                            >
                                                <SaleOffBadge descElement={
                                                    <span className="flex gap-2 text-xs p-0.5 text-white">
                                                        <ArrowLeftIcon

                                                            className="h-4 w-4"
                                                        />
                                                        Back
                                                    </span>
                                                } className="!bg-custom-200 dark:text-black font-semibold" />
                                            </button>
                                        </div>
                                    )}

                                    {(video && isWatchingVideo) && (
                                        <>
                                            <div className="hidden lg:block">
                                                <ReactPlayer url={video} width={"100%"} height={"450px"} stopOnUnmount={true} />
                                            </div>
                                            <div className="block lg:hidden">
                                                <ReactPlayer url={video} width={"95vw"} height={"460px"} stopOnUnmount={true} />
                                            </div>
                                        </>
                                    )}

                                    {!isWatchingVideo && (
                                        <>
                                            <div className="relative w-full">
                                                <GallerySlider
                                                    onClick={() => {
                                                        if (PHOTOS.length > 0) handleOpenModalImageGallery(0)
                                                    }}
                                                    uniqueID={`CarCard_${carId}`}
                                                    galleryImgs={PHOTOS}
                                                    placeholderClassName="w-full h-full object-contain min-h-[260px] lg:min-h-[340px]"
                                                    imgClassname="w-full h-full object-contain min-h-[260px] lg:min-h-[340px]"
                                                    hideDots={true}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="relative items-center mx-auto mt-4 mb-5 gap-x-3 px-3 hidden lg:flex">
                                        <span className="text-[.7rem] font-display text-custom-500 dark:text-custom-400">
                                            Share
                                        </span>

                                        <SocialsList
                                            sharer={window.location.href}
                                            className="text-[1.5rem]"
                                            itemClass="w-7 h-7 bg-white rounded-full dark:text-black flex items-center justify-center"
                                        />
                                    </div>
                                </div>

                                <div className="pb-10 hidden lg:block">
                                    {renderVehicleDetails()}
                                </div>
                            </div>


                            {/* mini slider */}
                            {PHOTOS.length > 8 && (
                                <div className="relative mx-auto z-10 overflow-hidden">
                                    <div className="flex space-x-1 relative w-full px-3">
                                        {PHOTOS.slice(1, 6).map((item: string, index: number) => (
                                            <div
                                                key={index}
                                                className={`relative w-16 h-16 md:w-36 md:h-32 lg:w-40 lg:h-32 xl:w-48 xl:h-40 cursor-pointer 
                                            ${index > 3 && "hidden lg:block"}
                                            `}
                                                onClick={() => { handleOpenModalImageGallery(index + 1) }}
                                            >
                                                <img
                                                    loading="lazy"
                                                    className="absolute object-cover h-full w-full"
                                                    src={item}
                                                    alt=""
                                                />
                                            </div>
                                        ))}

                                        {/* show all */}
                                        <div className="relative w-16 h-16 md:w-36 md:h-32 lg:w-40 lg:h-32 xl:w-48 xl:h-40 cursor-pointer overflow-hidden" onClick={() => { handleOpenModalImageGallery() }}>
                                            <img className="absolute object-cover h-full w-full" src={PHOTOS[6]} alt="Gallery" loading="lazy" />
                                            <div className="absolute w-full h-16 md:h-32 lg:h-32 xl:h-40 bg-custom-900 flex items-center justify-center bg-opacity-80">
                                                <span className="text-black dark:text-white text-base lg:text-xl font-medium">+{PHOTOS.length - 6}</span>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            )}


                            <div className="relative flex lg:hidden md:hidden items-center mt-4 mb-5 gap-x-3 px-3">
                                <span className="text-[.7rem] font-display text-custom-500 dark:text-custom-400">
                                    Share
                                </span>

                                <SocialsList
                                    sharer={window.location.href}
                                    className="text-[1.5rem]"
                                    itemClass="w-7 h-7 bg-white rounded-full dark:text-black flex items-center justify-center"
                                />
                            </div>

                            <div className="pt-0 px-10 md:pt-5 lg:pt-5 md:px-4 w-3/4 mx-auto lg:px-0 hidden lg:block">
                                {renderDescription()}
                            </div>
                        </div>

                        <main className="px-4 max-w-[1320px] mx-auto w-full relative z-10 mt-11 flex flex-col lg:flex-row gap-x-6">
                            <div className="w-full lg:w-4/5 xl:w-4/5 lg:space-y-10 mx-auto">
                                <div className="lg:hidden">
                                    <div className="relative z-10 flex flex-col space-y-2 text-neutral-700 dark:text-zinc-100">
                                        {carInfo.meta && carInfo.meta?.advertiserId === "10035437" && (
                                            <div className="w-fit mb-4">
                                                <SaleOffBadge desc="Cotswolds" className="bg-white dark:text-black font-bold !text-base" />
                                            </div>
                                        )}
                                        <h2 className="text-3xl md:text-4xl font-semibold text-left">
                                            {carInfo.make} {carInfo.model}
                                        </h2>
                                        <span className="text-lg font-light text-gray-200 mt-2 text-left">
                                            {carInfo.derivative}
                                        </span>
                                        <span className="text-xs font-light text-gray-400 mt-2 text-left">
                                            {carInfo.attentionGrabber}
                                        </span>
                                        <div className="flex items-end pt-6">
                                            <span className="text-4xl font-semibold mr-2">
                                                £{carInfo.priceGBP}
                                            </span>
                                            {carInfo.monthlyPayment && <span className="text-base text-gray-400 font-medium"> or {`£${carInfo.monthlyPayment}PM`}</span>}
                                        </div>
                                    </div>

                                    <div className="flex mx-auto gap-4 mt-6 mb-10">
                                        <Link to={"tel:+44 (0)1423 798781"}
                                            className={"bg-transparent w-fit text-center border hover:bg-custom-200 border-black dark:border-white text-black dark:text-white p-2 px-7 rounded-3xl font-medium text-[.6rem] font-display tracking-wider uppercase"}>
                                            <i className="fal fa-phone-alt mr-2"></i>
                                            Call
                                        </Link>
                                        <Link
                                            to={"#enquire"}
                                            onClick={() => {
                                                //goto #enquire div
                                                const el = document.getElementById("enquire");
                                                if (el) {
                                                    el.scrollIntoView({ behavior: "smooth" });
                                                }
                                            }}
                                            className={"bg-transparent w-fit  text-center border hover:bg-custom-200 border-black dark:border-white text-black dark:text-white p-2 px-7 rounded-3xl font-medium text-[.6rem] font-display tracking-wider uppercase"}>
                                            <i className="fal fa-envelope mr-2"></i>
                                            Enquire
                                        </Link>
                                    </div>

                                    <div className="mt-6 mb-10">
                                        {renderDescription(true)}
                                    </div>
                                </div>

                                {renderVehicleInfo()}

                                <div className="hidden md:flex lg:flex flex-col">
                                    {renderTabs()}
                                    {renderActiveTab()}
                                </div>

                                <div className="flex flex-col md:hidden lg:hidden">
                                    {renderFeatures()}
                                    {renderSidebarPrice()}
                                </div>
                            </div>

                        </main>
                        <CarGallery images={PHOTOS.slice(5, 12)} />
                    </div>
                )}

                <div className="max-w-3xl m-auto mt-20">
                    <EnquirePopUp showPopup={true} carData={carInfo} onClose={() => { }} asModal={false} />
                </div>

                <div className="mt-14">
                    <SectionSocials />
                    <SectionSubscribe2 />
                </div>
            </div>
        </Suspense>
    );
};


const CarGallery = ({ images }: { images: string[] }) => {
    if (images.length === 0) return null

    return (
        <div className="max-w-[1200px] mx-auto p-4">
            {/* For desktop: grid layout */}
            <div className="hidden md:grid grid-cols-2 gap-4 my-6">
                {images.splice(0, 2).map((image, index) => (
                    <img
                        key={index}
                        src={image}
                        alt={`Car ${index + 1}`}
                        className="w-full object-cover"
                    />
                ))}
            </div>

            <div className="hidden md:flex my-6">
                <img
                    src={images[2]}
                    alt={`Car 3`}
                    className="w-full object-contain"
                />
            </div>

            <div className="hidden md:grid grid-cols-2 gap-4 my-6">
                {images.splice(3, 5).map((image, index) => (
                    <img
                        key={index}
                        src={image}
                        alt={`Car ${index + 1}`}
                        className="w-full object-cover"
                    />
                ))}
            </div>

            {/* For mobile: single-column layout */}
            <div className="md:hidden flex flex-col gap-4">
                {images.map((image, index) => (
                    <img
                        key={index}
                        src={image}
                        alt={`Car ${index + 1}`}
                        className="w-full object-cover"
                    />
                ))}
            </div>
        </div>
    );
};


export default CarProfile
